import { createAsyncThunk } from '@reduxjs/toolkit'
import { findIndex, get, isEmpty } from 'lodash'

import { LabelingState, ThunkConfig } from '@/stores/slices/Labeling/types'
import { API } from '@/api'
import { LabelingNodeItem } from '@/api/LabelingNode/types'
import { socket } from '@/socket'

export const filterLabelingItems = createAsyncThunk<{
  itemList: LabelingNodeItem[],
  filters: LabelingState['fileFilter']
  targetLabelingItemId?: string
},
{
  filters: LabelingState['fileFilter'],
  isClassification?: boolean
  targetLabelingItemId?: string
}
, ThunkConfig>(
  'labeling/filter-labeling-items',
  async ({ filters, isClassification }, thunkAPI) => {
    if (filters.length < 1) {
      return { itemList: [], filters: [] }
    }

    const { originalFiles, hasSelectedForClustering, labelingNodeId, classTagFilters } = thunkAPI.getState().labeling

    const MAX_FILES_PER_PAGE = 1000 // FIXME: 값을 어느정도로 해야할지 정해야함
    const itemList = []
    let page = 0

    let res
    while (page >= 0) {
      if (isClassification) {
        // eslint-disable-next-line no-await-in-loop
        res = await API().Labeling.getClassificationItems({
          labelingNodeId,
          page,
          itemsPerPage: MAX_FILES_PER_PAGE,
          status: !isEmpty(filters) ? filters.join(',') : undefined,
          classes: !isEmpty(classTagFilters) ? classTagFilters.join(',') : undefined,
          isSelectedForClustering: hasSelectedForClustering,
        })
      } else {
        // eslint-disable-next-line no-await-in-loop
        res = await API().Labeling.getLabelingItems(labelingNodeId, {
          page,
          itemsPerPage: MAX_FILES_PER_PAGE,
          status: !isEmpty(filters) ? filters.join(',') : undefined,
          isSelectedForClustering: hasSelectedForClustering,
        })
      }

      itemList.push(...res.list)
      if (MAX_FILES_PER_PAGE * (page + 1) >= res.paging.total_count) {
        page = -1
      } else {
        page += 1
      }
    }

    if (itemList.length > 0) {
      await API().Labeling.updateUserMeta({
        labelingNodeId,
        lastIndex: findIndex(originalFiles, { _id: itemList[0]._id }),
        lastLabelingItemId: itemList[0]._id,
        duration: 0,
      })
    }

    return { itemList, filters }
  }
)

// 리턴, 디스패치 인자, thunkAPI 타입
export const toggleFileFinished = createAsyncThunk<LabelingNodeItem, void, ThunkConfig>(
  'labeling/toggle-file-finished',
  async (_, thunkAPI) => {
    const { currentIndex, files, labelingNodeId } = thunkAPI.getState().labeling
    const { user } = thunkAPI.getState().user
    const currentItemId = get(files[currentIndex], '_id', '')

    try {
      const res = await API().Labeling.completeLabelingItem(currentItemId)
      // socket
      socket?.Labeling.emitFinishLabelingItem({
        nodeId: labelingNodeId,
        userId: user._id,
        userName: user.name,
        itemId: res._id,
        updatedStatus: res.status,
      } as any)
      return res
    } catch (e: any) {
      const uniqueCode = e?.response?.data?.unique_code
      const errorMessage = e?.response?.data?.message || e.message
      return thunkAPI.rejectWithValue({
        uniqueCode,
        message: errorMessage,
      })
    }
  }
)

export const invalidateCompletedItems = createAsyncThunk<LabelingNodeItem[], void, ThunkConfig>(
  'labeling/invalidate-completed-items',
  async (_, thunkAPI) => {
    const { labelingNodeId, hasSelectedForClustering } = thunkAPI.getState().labeling
    const res = await API().Labeling.getLabelingItems(labelingNodeId, {
      page: 0,
      itemsPerPage: 20,
      // status: undefined,
      isSelectedForClustering: hasSelectedForClustering,
    })
    return res.list
  }
)
